import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { XeroApiService } from './xero-api.service';
import { SharedService } from './shared.service';
import { AuthProvider } from 'src/app/models';
import { Conversion } from 'src/app/models/conversion.model';
import { XeroFeedConnection } from 'src/app/models/xero.model';

/** A Xero bank account, as returned by the accounts picker endpoint */
export interface XeroBankAccount {
  accountId: string;
  name: string;
  code: string;
  bankAccountNumber: string | null;
  currencyCode: string | null;
}

/** Org context returned alongside the accounts (incl. Xero's own add-account URL) */
export interface XeroOrgContext {
  name: string;
  shortCode: string | null;
  baseCurrency: string | null;
  addBankAccountUrl: string | null;
}

export interface XeroAccountsResult {
  accounts: XeroBankAccount[];
  organisation: XeroOrgContext | null;
}

/** Xero scope required to push statements — see XeroAuthService.BANKFEEDS_SCOPE */
const BANKFEEDS_SCOPE = 'bankfeeds';

/**
 * Whether a connection can actually send statements today. Connections made
 * before the bankfeeds scope existed look perfectly healthy but can never mint
 * a bank-feeds token, so they need a fresh consent just like an expired one.
 * The backend enforces the same rule; this only lets the UI say so up front.
 */
export function canSendToXero(provider: AuthProvider | null): boolean {
  if (!provider?.isActive || provider.needsReauth || !provider.tenants?.length) return false;
  return !!provider.tokens?.scopes?.includes(BANKFEEDS_SCOPE);
}

/** Target for a statement push: an existing Xero bank account */
export interface XeroPushTarget {
  tenantId: string;
  accountId: string;
  currency?: string;
  /** Org short code from the accounts response — stored for the reconcile deep link */
  shortCode?: string;
}

export type XeroPushState = NonNullable<Conversion['xeroPush']>;

/**
 * Deep link to Xero's reconciliation screen for the account a statement was
 * delivered into. Pure helper so any component can render the link without
 * injecting the service. Returns null when the push predates accountId tracking.
 */
export function xeroReconcileUrl(xeroPush: Conversion['xeroPush']): string | null {
  if (!xeroPush?.accountId) return null;
  const reconcilePath = `/BankRec/BankRec.aspx?accountID=${xeroPush.accountId}`;
  // Xero's documented deep-link form: the shortcode selects the organisation
  // first, so the link works even when the user has several orgs.
  return xeroPush.shortCode
    ? `https://go.xero.com/organisationlogin/default.aspx?shortcode=${encodeURIComponent(xeroPush.shortCode)}&redirecturl=${encodeURIComponent(reconcilePath)}`
    : `https://go.xero.com${reconcilePath}`;
}

/**
 * Result of the pre-push eligibility check. Returned as data (never as an
 * error) so the dialog can render a proper explanation before the user
 * invests any effort picking a destination.
 */
export interface XeroPushPreflight {
  eligible: boolean;
  code?: string; // e.g. XERO_STATEMENT_TOO_OLD, XERO_ALREADY_DELIVERED
  reason?: string; // human-readable explanation when not eligible
  summary?: {
    startDate: string;
    endDate: string;
    lineCount: number;
    startBalance: number; // signed
    endBalance: number; // signed
  };
  xeroPush?: XeroPushState; // present if this conversion was pushed before
}

const ACCOUNTS_CACHE_PREFIX = 'xeroBankAccounts_';
const ACCOUNTS_CACHE_TTL_MS = 10 * 60 * 1000; // bank accounts change rarely

interface AccountsCacheEntry {
  result: XeroAccountsResult;
  fetchedAt: number;
}

/**
 * Service for pushing converted statements into Xero bank feeds
 */
@Injectable({
  providedIn: 'root',
})
export class XeroBankFeedService {
  private prefetchStarted = false;

  /**
   * Single-flight session cache of the provider doc. Every conversion row asks
   * whether Xero is connected as it mounts, and an infinite-scroll list mounts
   * many at once — they all share this one read.
   */
  private cachedProvider: Promise<AuthProvider | null> | null = null;

  constructor(
    private xeroApiService: XeroApiService,
    private firestore: AngularFirestore,
    private ss: SharedService,
  ) {}

  /** The user's Xero provider doc (tenants, connection state), read fresh */
  async getProvider(): Promise<AuthProvider | null> {
    const user = await this.ss.getUser();
    if (!user?.uid) return null;
    const doc = await this.firestore.doc<AuthProvider>(`authProviders/${user.uid}_xero`).get().toPromise();
    return doc.exists ? (doc.data() as AuthProvider) : null;
  }

  /**
   * Whether the user has a Xero connection that can push statements today
   * (active, not needing re-consent, and carrying the bankfeeds scope).
   * Cached — use it for render-time gating; the dialog re-reads authoritatively.
   */
  async isConnected(): Promise<boolean> {
    return canSendToXero(await this.getCachedProvider());
  }

  /** Forget the cached connection — the next isConnected() re-reads Firestore */
  refreshConnection(): void {
    this.cachedProvider = null;
    this.prefetchStarted = false;
  }

  private getCachedProvider(): Promise<AuthProvider | null> {
    if (!this.cachedProvider) {
      this.cachedProvider = this.getProvider().catch(() => {
        // Don't poison the cache with a failed read — retry on the next ask
        this.cachedProvider = null;
        return null;
      });
    }
    return this.cachedProvider;
  }

  /**
   * Fire-and-forget cache warm-up so the send dialog opens with accounts
   * already loaded. Called when a pushable row renders; runs at most once
   * per app session and swallows all errors (purely an optimization).
   */
  prefetchAccounts(): void {
    if (this.prefetchStarted) return;
    this.prefetchStarted = true;

    this.getCachedProvider()
      .then((provider) => {
        if (!canSendToXero(provider)) return;
        return Promise.all(provider.tenants.map((t) => this.getBankAccounts(t.tenantId)));
      })
      .catch(() => undefined);
  }

  /**
   * List the org's (non-archived) bank accounts + org context.
   * Stale-while-revalidate: cached results (sessionStorage) are returned
   * immediately and refreshed in the background, so the picker is instant
   * after the first load. `onRefresh` delivers the fresh list when it lands.
   * Resolves null when the fetch failed (the error has already been surfaced).
   */
  async getBankAccounts(tenantId: string, onRefresh?: (result: XeroAccountsResult) => void): Promise<XeroAccountsResult | null> {
    const cached = this.readAccountsCache(tenantId);

    if (cached) {
      const isFresh = Date.now() - cached.fetchedAt < ACCOUNTS_CACHE_TTL_MS;
      if (!isFresh) {
        // Refresh in the background; don't block the picker. A failed refresh
        // resolves null and is ignored — the cached list stays on screen.
        this.fetchBankAccounts(tenantId).then((result) => result && onRefresh?.(result));
      }
      return cached.result;
    }

    return this.fetchBankAccounts(tenantId);
  }

  /** Force-refresh the accounts list (e.g. after the user added an account in Xero) */
  async refreshBankAccounts(tenantId: string): Promise<XeroAccountsResult | null> {
    return this.fetchBankAccounts(tenantId);
  }

  /** Check up-front whether a conversion can be pushed, and get its statement summary */
  async preflight(fileId: string): Promise<XeroPushPreflight | null> {
    return this.xeroApiService.post<XeroPushPreflight>(
      'xero-xeroBankFeed/push-conversion/preflight',
      { fileId },
      { actionDescription: 'check the statement' },
    );
  }

  /** Push a conversion's statement into the chosen Xero account */
  async pushConversion(fileId: string, target: XeroPushTarget): Promise<{ xeroPush: XeroPushState } | null> {
    return this.xeroApiService.post<{ xeroPush: XeroPushState }>(
      'xero-xeroBankFeed/push-conversion',
      { fileId, ...target },
      { actionDescription: 'send the statement to Xero' },
    );
  }

  /**
   * Sync the delivery status (PENDING → DELIVERED/REJECTED) from Xero.
   * Xero delivers asynchronously and never calls us back, so this is the only
   * thing that settles a push. Pass `silent` for background reconciliation.
   */
  async refreshStatus(fileId: string, silent = false): Promise<{ xeroPush: XeroPushState } | null> {
    return this.xeroApiService.post<{ xeroPush: XeroPushState }>(
      'xero-xeroBankFeed/push-conversion/refresh-status',
      { fileId },
      { actionDescription: 'check the Xero delivery status', silent },
    );
  }

  /**
   * Get all feed connections for a tenant
   */
  async getFeedConnections(tenantId: string): Promise<XeroFeedConnection[]> {
    const response = await this.xeroApiService.get<{ items: XeroFeedConnection[] }>(
      'xero-xeroBankFeed/feed-connections',
      { actionDescription: 'load feed connections' },
      { tenantId },
    );
    return response?.items || [];
  }

  /**
   * Delete a feed connection
   */
  async deleteFeedConnection(tenantId: string, feedConnectionId: string): Promise<void> {
    await this.xeroApiService.post(
      'xero-xeroBankFeed/feed-connections/delete',
      { items: [{ id: feedConnectionId }] },
      { actionDescription: 'delete feed connection' },
      { tenantId },
    );
  }

  // Accounts cache (sessionStorage, stale-while-revalidate)

  private async fetchBankAccounts(tenantId: string): Promise<XeroAccountsResult | null> {
    const response = await this.xeroApiService.get<XeroAccountsResult>(
      'xero-xeroBankFeed/accounts',
      { actionDescription: 'load Xero bank accounts' },
      { tenantId },
    );
    if (response) this.writeAccountsCache(tenantId, response);
    return response;
  }

  private readAccountsCache(tenantId: string): AccountsCacheEntry | null {
    try {
      const raw = sessionStorage.getItem(ACCOUNTS_CACHE_PREFIX + tenantId);
      return raw ? (JSON.parse(raw) as AccountsCacheEntry) : null;
    } catch {
      return null;
    }
  }

  private writeAccountsCache(tenantId: string, result: XeroAccountsResult): void {
    try {
      const entry: AccountsCacheEntry = { result, fetchedAt: Date.now() };
      sessionStorage.setItem(ACCOUNTS_CACHE_PREFIX + tenantId, JSON.stringify(entry));
    } catch {
      // sessionStorage unavailable/full — caching is best-effort
    }
  }
}
